import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { api } from '../api/client'
import { Button, Field, Input, PageHeader } from '../components/ui'

function useChangePassword() {
  return useMutation({
    mutationFn: (body: { current_password: string; new_password: string }) =>
      api.post('/auth/password', body),
  })
}

export default function ChangePasswordPage() {
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [confirm, setConfirm] = useState('')
  const [success, setSuccess] = useState(false)
  const mutation = useChangePassword()
  const { t } = useTranslation()

  const validationError =
    next.length > 0 && next.length < 8
      ? t('changePassword.minLength')
      : next !== confirm && confirm.length > 0
      ? t('changePassword.mismatch')
      : null

  const canSubmit = current.length > 0 && next.length >= 8 && next === confirm && !mutation.isPending

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!canSubmit) return
    setSuccess(false)
    mutation.mutate(
      { current_password: current, new_password: next },
      {
        onSuccess: () => {
          setSuccess(true)
          setCurrent('')
          setNext('')
          setConfirm('')
        },
      },
    )
  }

  return (
    <div className="p-4 sm:p-8 mx-auto w-full max-w-md">
      <PageHeader title={t('changePassword.title')} />

      <form onSubmit={handleSubmit} className="space-y-4">
        <Field label={t('changePassword.current')}>
          {(p) => (
            <Input
              {...p}
              type="password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              autoComplete="current-password"
            />
          )}
        </Field>

        <Field label={t('changePassword.new')}>
          {(p) => (
            <Input
              {...p}
              type="password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              autoComplete="new-password"
            />
          )}
        </Field>

        <Field label={t('changePassword.confirm')} error={validationError ?? undefined}>
          {(p) => (
            <Input
              {...p}
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
              invalid={Boolean(validationError)}
            />
          )}
        </Field>

        {mutation.isError && (
          <p className="text-sm text-red-600">
            {(mutation.error as { response?: { data?: { error?: string } } })?.response?.data?.error ??
              t('changePassword.error')}
          </p>
        )}

        {success && <p className="text-sm text-green-600">{t('changePassword.success')}</p>}

        <Button type="submit" disabled={!canSubmit} loading={mutation.isPending} fullWidth>
          {mutation.isPending ? t('changePassword.submitting') : t('changePassword.submit')}
        </Button>
      </form>
    </div>
  )
}
